import { ColumnDef } from "@tanstack/react-table";
import React, { useMemo } from "react";

import { Badge } from "../../../../components/badge/Badge";
import { SettingsIconButton } from "../../../../components/button/SettingsIconButton";
import { SettingsCard } from "../../../../components/card/SettingsCard";
import { SettingsDropdown } from "../../../../components/dropdown/SettingsDropdown";
import { LottieLoading } from "../../../../components/graphics/LottieLoading";
import { DeactivateSvg } from "../../../../components/svg/DeactivateSvg";
import { DotsSvg } from "../../../../components/svg/DotsSvg";
import { EditPencilSvg } from "../../../../components/svg/EditPencilSvg";
import { TrashSvg } from "../../../../components/svg/TrashSvg";
import { Table } from "../../../../components/table/Table";
import { WorkTypeModel } from "../../../../factories/work-type/models/work-type.model";
import { compareAlphabeticallyWithNumbersFirst } from "../../../../utils/sort";
import { WorkTypesTableProps } from "../model/workTypesList.types";

export const WorkTypesTable: React.FC<WorkTypesTableProps> = ({
  isLoading,
  workTypes,
  onEdit,
  onDeactivate,
  onActivate,
  onDelete,
}) => {
  const workTypeColumns = useMemo<ColumnDef<WorkTypeModel>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Work Type",
        sortingFn: (rowA, rowB, columnId) =>
          compareAlphabeticallyWithNumbersFirst(
            rowA.getValue(columnId),
            rowB.getValue(columnId),
          ),
        meta: {
          className: "text-[#1D2C3B] font-semibold",
        },
      },
      {
        accessorKey: "description",
        header: "Description",
      },
      {
        id: "statusesCount",
        header: "Assigned Statuses",
        accessorFn: (row: WorkTypeModel) => {
          const filteredStatuses = row.serviceStatuses.filter(
            (s) => s.name.toLowerCase() !== "trial",
          );
          const statusesCount = filteredStatuses.length;
          const subStatusesCount = filteredStatuses.reduce(
            (acc, status) => acc + (status.serviceSubStatuses?.length || 0),
            0,
          );

          return statusesCount + subStatusesCount;
        },
        cell: ({ getValue }) => {
          const count = getValue<number>();
          return <span className={"text-gray-600"}>{count}</span>;
        },
        sortingFn: "basic",
        enableSorting: true,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <Badge
            label={row.original.isActive ? "Active" : "Inactive"}
            variant={row.original.isActive ? "status-green" : "status-grey"}
          />
        ),
      },
      {
        accessorKey: "createdAt",
        header: "Date Created",
        cell: ({ getValue }) => {
          const raw = getValue() as string;
          const date = new Date(raw);

          const formatted = date.toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour12: true,
          });

          return <span className={"text-gray-600"}>{formatted}</span>;
        },
        sortingFn: (a, b, colId) =>
          new Date(a.getValue(colId)).getTime() -
          new Date(b.getValue(colId)).getTime(),
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <div
            className={"flex justify-end"}
            onClick={(event) => event.stopPropagation()}>
            <SettingsDropdown
              actionsList={[
                {
                  name: "Edit",
                  action: () => onEdit(row.original),
                  icon: <EditPencilSvg />,
                },
                {
                  name: row.original.isActive ? "Deactivate" : "Activate",
                  action: () =>
                    row.original.isActive
                      ? onDeactivate(row.original)
                      : onActivate(row.original),
                  icon: <DeactivateSvg />,
                },
                {
                  name: "Delete",
                  action: () => onDelete(row.original),
                  icon: <TrashSvg />,
                  extraClasses: "text-[#DC2626]",
                  iconColor: "text-[#DC2626]",
                },
              ]}>
              <SettingsIconButton icon={<DotsSvg />} onClick={() => null} />
            </SettingsDropdown>
          </div>
        ),
      },
    ],
    [onActivate, onDeactivate, onDelete, onEdit],
  );

  return (
    <SettingsCard>
      {isLoading ? (
        <LottieLoading />
      ) : (
        <Table
          data={workTypes}
          columns={workTypeColumns}
          initialSorting={[{ id: "name", desc: false }]}
          onRowClick={onEdit}
          rowClassName={"transition-colors hover:bg-gray-100"}
        />
      )}
    </SettingsCard>
  );
};
